import * as React from 'react';

import { handleArrowDown, handleEnter, handleTab } from '../Utils/events/events';
import { VALID_DIMENSION_DATA } from '../Utils/Styles/calcDimensionStyles';

import { useDropdown } from './useDropdown';
import { ActionTypes, IItem, ListboxReducer, listboxReducer, useListbox } from './useListbox';

export type IndexedItem = IItem & { index: number };

// tslint:disable max-func-body-length
export const useAutoComplete = (items: IItem[], onSelect?: (index: number) => void) => {
  const [inputValue, setInputValue] = React.useState('');
  const filterItems = (str: string) =>
    items.filter(
      item =>
        item.value.toLowerCase()
          .includes(str)
        || item.label.toLowerCase()
          .includes(str)
        // || (item.group && item.group.toLowerCase().includes(str))
    );
  const filteredItems = filterItems(inputValue.toLowerCase());

  const {
    isVisible,
    toggle,
    show,
    hide,
    labelProps,
    triggerProps: trigProps, // sets up a11y attributes and keydown for opening dropdown with down arrow
    containerProps: contProps, // sets up a11y attributes
  } = useDropdown('listbox');

  // disables option selection functionality by avoiding setting it's state
  const doNotRetainSelection: ListboxReducer = (prevState, action) => {
    // when a selection is made:
    //  set that option LABEL (is this ok? vs value) to the value of the input
    //  unset selected state so nothing gets added to selected array
    // hide the dropdown
    if (action.type === ActionTypes.select) {
      // console.log(action.payload, items)
      if (filteredItems[action.payload]) {
        setInputValue(filteredItems[action.payload].label);
      }
      prevState.selected = [];
      action.payload = -1;
      hide();
    }

    if (action.type === ActionTypes.folkus) {
      const isInFilteredItems = filteredItems[action.payload];
      action.payload = isInFilteredItems ? action.payload : prevState.folkused;
    }

    return listboxReducer(prevState, action);
  };

  // exposes listbox functionality and state management
  const {
    listboxOptionProps,
    listboxState,
    keydownHandler
  } = useListbox(filteredItems, [], doNotRetainSelection, onSelect);

  // trigger prop getter: overrides controlled props and merges event handlers
  const triggerProps = (attrOverrides: React.InputHTMLAttributes<HTMLInputElement> = {}): React.InputHTMLAttributes<HTMLInputElement> => ({
    ...attrOverrides,
    ...trigProps,
    'aria-autocomplete': 'list',
    // used when trigger opens a listbox
    'aria-activedescendant': listboxState.folkused >= 0 ? listboxOptionProps(listboxState.folkused).id : undefined,
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      handleArrowDown(ev => {
        ev.preventDefault();
        show();
      })(e);
      // if listbox is visible, use listbox's keydown handler to selecting an option with enter, arrow through options
      // else show the dropdown when you press enter inside of the input
      if (isVisible) {
        keydownHandler(e);
      } else {
        handleEnter(show)(e);
      }
      // to close when tabbing away (onBlur not an option here because it runs before an onClick on option
      handleTab(hide)(e);
      if (attrOverrides.onKeyDown) {
        attrOverrides.onKeyDown(e);
      }
    },
    onChange: e => {
      show();
      setInputValue(e.target.value);
      if (attrOverrides.onChange) {
        attrOverrides.onChange(e);
      }
    },
    onFocus: e => {
      show();
      if (attrOverrides.onFocus) {
        attrOverrides.onFocus(e);
      }
    },
    onClick: e => {
      show();
      if (attrOverrides.onClick) {
        attrOverrides.onClick(e);
      }
    },
    value: inputValue,
  });

  // container prop getter: overrides controlled props and merges keydown handlers
  const containerProps = (
      attrOverrides: React.HTMLAttributes<HTMLUListElement> & { width?: VALID_DIMENSION_DATA } = {}
    ): React.HTMLAttributes<HTMLUListElement> => ({
    ...attrOverrides,
    ...contProps,
  });

  return {
    isVisible,
    toggle,
    show,
    hide,
    labelProps,
    triggerProps,
    containerProps,
    listboxOptionProps,
    items: filteredItems,
    highlightedItem: listboxState.folkused,
    inputValue,
    setInputValue,
  };
};
